import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { InvoiceDialog } from "@/features/bookings/InvoiceDialog";
import { date, eur, hours } from "@/lib/format";
import type { Dashboard, OpenCustomer } from "@/lib/types";

function ShareBar({ value, max }: { value: number; max: number }) {
  return (
    <div aria-hidden className="h-1.5 w-full rounded-full bg-muted">
      <div className="h-full rounded-full bg-chart-2" style={{ width: `${max > 0 ? Math.max(2, (value / max) * 100) : 0}%` }} />
    </div>
  );
}

function Actions({ c, onInvoice }: { c: OpenCustomer; onInvoice: (c: OpenCustomer) => void }) {
  return (
    <div className="flex justify-end gap-1">
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/buchungen?client=${c.clientId}`}>Buchungen</Link>
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={c.entryCount === 0}
        title={c.entryCount === 0 ? "Nur Ausgaben-Anteile – über Ausgaben › Verrechnung abrechnen" : undefined}
        onClick={() => onInvoice(c)}
      >
        Rechnung…
      </Button>
    </div>
  );
}

export function OpenByCustomer({ d }: { d: Dashboard }) {
  const qc = useQueryClient();
  const [invoicing, setInvoicing] = useState<OpenCustomer | null>(null);
  const rows = d.open.customers;
  const max = rows[0]?.totalEur ?? 0;
  const t = d.open.totals;

  return (
    <section aria-labelledby="open-title" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="open-title" className="text-base font-semibold">Offen je Kunde</h2>
        <Link to="/buchungen" className="text-sm text-muted-foreground hover:text-foreground">Alle offenen Buchungen →</Link>
      </div>
      {d.open.onDrafts.note && (
        <p className="text-[0.8125rem] text-muted-foreground">
          {d.open.onDrafts.note} – <Link to="/rechnungen?stufe=draft" className="text-foreground underline underline-offset-4">Entwürfe</Link>
        </p>
      )}

      {rows.length === 0 ? (
        <p className="rounded-xl py-10 text-center text-sm text-muted-foreground ring-1 ring-border">Nichts offen – alles verrechnet.</p>
      ) : (
        <>
          <div className="hidden md:block">
            <Table className="table-fixed">
              <colgroup>
                <col /><col style={{ width: "6rem" }} /><col style={{ width: "7.5rem" }} /><col style={{ width: "7rem" }} />
                <col style={{ width: "9rem" }} /><col style={{ width: "7rem" }} /><col style={{ width: "12.5rem" }} />
              </colgroup>
              <TableHeader>
                <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
                  <TableHead>Kunde</TableHead>
                  <TableHead className="text-right">Stunden</TableHead>
                  <TableHead className="text-right">Stunden €</TableHead>
                  <TableHead className="text-right">Ausgaben €</TableHead>
                  <TableHead className="text-right">Summe</TableHead>
                  <TableHead>Offen seit</TableHead>
                  <TableHead><span className="sr-only">Aktionen</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.clientId} className="h-14">
                    <TableCell className="overflow-hidden">
                      <Link to={`/firmen/${c.clientId}`} className="truncate font-medium hover:underline underline-offset-2">{c.name}</Link>
                      <span className="block text-xs text-muted-foreground tabular-nums">
                        {c.entryCount > 0 && `${c.entryCount} ${c.entryCount === 1 ? "Buchung" : "Buchungen"}`}
                        {c.entryCount > 0 && c.expenseShareCount > 0 && " · "}
                        {c.expenseShareCount > 0 && `${c.expenseShareCount} ${c.expenseShareCount === 1 ? "Ausgabe" : "Ausgaben"}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.billableMinutes > 0 ? hours(c.billableMinutes) : "–"}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.hoursEur > 0 ? eur(c.hoursEur) : "–"}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.expenseEur > 0 ? eur(c.expenseEur) : "–"}</TableCell>
                    <TableCell>
                      <div className="space-y-1.5 text-right">
                        <span className="font-semibold tabular-nums">{eur(c.totalEur)}</span>
                        <ShareBar value={c.totalEur} max={max} />
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{date(c.oldest)}</TableCell>
                    <TableCell><Actions c={c} onInvoice={setInvoicing} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter className="bg-transparent">
                <TableRow className="hover:bg-transparent">
                  <TableCell className="text-muted-foreground">Summe</TableCell>
                  <TableCell className="text-right tabular-nums">{hours(t.billableMinutes)}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(t.hoursEur)}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(t.expenseEur)}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{eur(t.totalEur)}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            </Table>
          </div>

          <ul className="space-y-2 md:hidden">
            {rows.map((c) => (
              <li key={c.clientId} className="space-y-2 rounded-lg p-3 ring-1 ring-border">
                <div className="flex items-baseline justify-between gap-3">
                  <Link to={`/firmen/${c.clientId}`} className="truncate font-medium hover:underline underline-offset-2">{c.name}</Link>
                  <span className="shrink-0 font-semibold tabular-nums">{eur(c.totalEur)}</span>
                </div>
                <ShareBar value={c.totalEur} max={max} />
                <p className="text-sm text-muted-foreground tabular-nums">
                  {c.billableMinutes > 0 && `${hours(c.billableMinutes)} · `}
                  {c.expenseEur > 0 && `${eur(c.expenseEur)} Ausgaben · `}
                  seit {date(c.oldest)}
                </p>
                <Actions c={c} onInvoice={setInvoicing} />
              </li>
            ))}
          </ul>
        </>
      )}

      <InvoiceDialog
        entryIds={invoicing?.entryIds ?? []}
        open={invoicing !== null}
        onClose={() => setInvoicing(null)}
        onCreated={() => qc.invalidateQueries({ queryKey: ["dashboard"] })}
      />
    </section>
  );
}
